import { createContext, useEffect, useState } from 'react';
import { ApolloError } from '@apollo/client';

import {
  GameTypeEnum,
  OnlineOpponentsQuery_onlineOpponents_opponents,
} from 'graphql/types';
import {} from 'graphql/types';

import {
  POLL_INTERVAL,
  useOnlineOpponents,
  DEFAULT_RESULT_SIZE,
} from '../hooks/useOnlineOpponents';

const STOP_POLLING_ERRORS = [
  'INTERNAL_SERVER_ERROR',
  'GRAPHQL_PARSE_FAILED',
  'GRAPHQL_VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
];

interface OnlineUsersContextType {
  error?: ApolloError;
  opponents?: OnlineOpponentsQuery_onlineOpponents_opponents[];
  opponentCount?: number;
  searchedTerm: string;
  searchTerm?: (term: string, sceneId: string, gameType: GameTypeEnum) => void;
  resultSize: number;
  setResultSize?: (size: number) => void;
  clearSearch?: () => void;
}

export const OnlineUsersContext = createContext<OnlineUsersContextType>({
  opponents: [],
  opponentCount: 0,
  searchedTerm: '',
  resultSize: DEFAULT_RESULT_SIZE,
});

interface Props {
  children: React.ReactNode | null;
  value?: number;
}

export const MIN_SEARCH_TERM_LENGTH = 3;

export const OnlineUsersProvider = ({ children }: Props) => {
  const diacrRE = /[\u0300-\u036f]/g;
  const punctRE =
    /[\u2000-\u206F\u2E00-\u2E7F\\'!"#$%&()*+,\-./:;<=>?@[\]^_`{|}~]/g;
  const spaceRE = /\s+/g;

  const isSearchTermValid = (term?: string | null): boolean => {
    return !!(term && term.trim().length >= MIN_SEARCH_TERM_LENGTH);
  };

  const normalizeInput = (input: string): string => {
    return input
      .toLowerCase()
      .normalize('NFD')
      .replace(diacrRE, '')
      .replace(punctRE, '')
      .replace(spaceRE, ' ')
      .trim();
  };

  const [selectedSceneId, setSelectedSceneId] = useState<string | undefined>(
    undefined,
  );
  const [gameType, setGameType] = useState<GameTypeEnum | undefined>(undefined);
  const [resultSize, setResultSize] = useState(DEFAULT_RESULT_SIZE);
  const [searchedTerm, setSearchedTerm] = useState('');

  const {
    error,
    opponents,
    refetch,
    startPolling,
    stopPolling,
    opponentCount,
  } = useOnlineOpponents(
    selectedSceneId,
    gameType,
    resultSize,
    isSearchTermValid(searchedTerm) ? searchedTerm : null,
  );

  useEffect(() => {
    stopPolling();
    refetch();
    startPolling(POLL_INTERVAL);

    return () => {
      stopPolling();
    };
  }, [refetch, startPolling, stopPolling, opponents]);

  useEffect(() => {
    if (error) {
      if (
        error.graphQLErrors &&
        error.graphQLErrors.some((gqlError) => {
          return (
            gqlError.extensions &&
            STOP_POLLING_ERRORS.includes(gqlError.extensions.code)
          );
        })
      ) {
        stopPolling();
      }
    }
  }, [error, stopPolling]);

  useEffect(() => {
    refetch();
  }, [resultSize, refetch]);

  const searchTerm = (
    term: string,
    sceneId: string,
    gameType: GameTypeEnum,
  ) => {
    setSearchedTerm(normalizeInput(term));
    setSelectedSceneId(sceneId);
    setGameType(gameType);
  };

  const clearSearch = () => {
    if (searchedTerm) {
      // Clear when there is any search value
      setSearchedTerm('');
      setSelectedSceneId(undefined);
      setGameType(undefined);
      setResultSize(DEFAULT_RESULT_SIZE);
    }
  };

  return (
    <OnlineUsersContext.Provider
      value={{
        error,
        opponents,
        opponentCount,
        searchTerm,
        searchedTerm,
        resultSize,
        setResultSize,
        clearSearch,
      }}
    >
      {children}
    </OnlineUsersContext.Provider>
  );
};
